import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { propertyService } from 'api/services/propertyService';
import { useRoomCategories, useDeleteRoomCategory } from 'hooks/api/useRoomCategory';
import type { RoomCategory, RoomCategorySearchRequest, Property } from 'api/types';
import RoomCategoryModal from 'components/modals/modules/room-category-module/RoomCategoryModal';
import RoomCategoryViewModal from 'components/modals/modules/room-category-module/RoomCategoryViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const bedTypeFilterOptions = [
  { value: '', label: 'All Bed Types' },
  { value: 'King', label: 'King' },
  { value: 'Queen', label: 'Queen' },
  { value: 'Twin', label: 'Twin' },
  { value: 'Double', label: 'Double' },
  { value: 'Single', label: 'Single' },
  { value: 'Bunk', label: 'Bunk' },
];

const roomViewFilterOptions = [
  { value: '', label: 'All Views' },
  { value: 'Sea', label: 'Sea View' },
  { value: 'Garden', label: 'Garden View' },
  { value: 'Pool', label: 'Pool View' },
  { value: 'City', label: 'City View' },
  { value: 'Mountain', label: 'Mountain View' },
  { value: 'No View', label: 'No View' },
];

const smokingFilterOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'categoryCode', label: 'Category Code', field: 'categoryCode', required: false, defaultVisible: true, sortable: true },
  { id: 'categoryName', label: 'Category Name', field: 'categoryName', required: false, defaultVisible: true, sortable: true },
  { id: 'property', label: 'Property', field: 'propertyId', required: false, defaultVisible: true, sortable: false },
  { id: 'bedType', label: 'Bed Type', field: 'bedType', required: false, defaultVisible: true, sortable: true },
  { id: 'maxOccupancy', label: 'Max Occupancy', field: 'maxOccupancy', required: false, defaultVisible: true, sortable: true },
  { id: 'totalInventory', label: 'Total Inventory', field: 'totalInventory', required: false, defaultVisible: true, sortable: true },
  { id: 'smokingAllowed', label: 'Smoking', field: 'smokingAllowed', required: false, defaultVisible: true, sortable: true },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'baseOccupancy', label: 'Base Occupancy', field: 'baseOccupancy', required: false, defaultVisible: false, sortable: true },
  { id: 'roomView', label: 'Room View', field: 'roomView', required: false, defaultVisible: false, sortable: true },
  { id: 'roomSizeSqft', label: 'Room Size (sqft)', field: 'roomSizeSqft', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 0.5rem)',
          right: 0,
          width: '320px',
          maxHeight: '500px',
          background: 'var(--bg-primary)',
          border: '2px solid var(--primary)',
          borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          animation: 'slideDown 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '2px solid var(--border-color)',
            background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              Manage Columns
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div
                key={column.id}
                onClick={() => toggleColumn(column.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.625rem 1.25rem',
                  cursor: column.required ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  gap: '0.75rem',
                  opacity: column.required ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: '1.125rem',
                  height: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--border-color)',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            padding: '1rem',
            borderTop: '2px solid var(--border-color)',
            background: 'var(--bg-primary)',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// ROOM CATEGORY CARD COMPONENT (Grid View)
// ============================================================================

interface RoomCategoryCardProps {
  roomCategory: RoomCategory;
  onView: (rc: RoomCategory) => void;
  onEdit: (rc: RoomCategory) => void;
  onCopy: (rc: RoomCategory) => void;
  onDelete: (rc: RoomCategory) => void;
}

const RoomCategoryCard: React.FC<RoomCategoryCardProps> = ({ roomCategory, onView, onEdit, onCopy, onDelete }) => {
  const menuItems: ActionMenuItem[] = [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(roomCategory) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(roomCategory) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => onCopy(roomCategory) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(roomCategory), variant: 'danger' },
  ];

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {roomCategory.categoryCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {roomCategory.categoryName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Bed Type</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{roomCategory.bedType || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Max Occupancy</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{roomCategory.maxOccupancy ?? '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Total Inventory</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{roomCategory.totalInventory ?? '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${roomCategory.isActive ? 'active' : 'inactive'}`}>
            {roomCategory.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface RoomCategoryMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const RoomCategoryMasterScreen: React.FC<RoomCategoryMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<RoomCategorySearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, refetch } = useRoomCategories(queryParams);
  const { deleteRoomCategory, loading: isDeleting } = useDeleteRoomCategory();

  // --- Property lookup (resolve IDs to names for grid display) ---
  const [propertyLookup, setPropertyLookup] = useState<Record<string, Property>>({});
  const loadLookups = useCallback(async () => {
    try {
      const res = await propertyService.getAll({ pageSize: 500 });
      const lookup: Record<string, Property> = {};
      (res.data || []).forEach(p => { lookup[p.id] = p; });
      setPropertyLookup(lookup);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadLookups(); }, [loadLookups]);

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterProperty, setFilterProperty] = useState('');
  const [filterBedType, setFilterBedType] = useState('');
  const [filterRoomView, setFilterRoomView] = useState('');
  const [filterSmoking, setFilterSmoking] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<RoomCategory | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Search debounce (server-side, 400ms) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Server-side pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('room-category-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('room-category-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('room-category-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- F2 keyboard shortcut for search popup ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // --- View handler ---
  const handleView = useCallback((rc: RoomCategory) => {
    const viewId = tabId ? `${tabId}-view-modal-${rc.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(rc);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((categoryId: string) => {
    loadLookups();
    const rc = data.find(r => r.id === categoryId);
    if (rc) {
      setSelectedRecord(rc);
      setViewModalOpen(true);
    }
  }, [data, loadLookups]);

  // --- Edit handler ---
  const handleEdit = useCallback((rc: RoomCategory) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${rc.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(rc);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Copy handler (multi-instance) ---
  const handleCopy = useCallback((rc: RoomCategory) => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(rc);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((rc: RoomCategory) => {
    setSelectedRecord(rc);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteRoomCategory(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.categoryName}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteRoomCategory, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
    loadLookups();
  }, [refetch, loadLookups]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, categoryCode?: string) => {
    loadLookups();
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (categoryCode) {
      const rc = data.find(r => r.categoryCode === categoryCode);
      if (rc) setSelectedRecord(rc);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data, loadLookups]);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<RoomCategory>[] = useMemo(() => [
    { id: 'categoryCode', header: 'Category Code', accessorKey: 'categoryCode', size: 130, enableSorting: true },
    { id: 'categoryName', header: 'Category Name', accessorKey: 'categoryName', size: 200, enableSorting: true },
    {
      id: 'property',
      header: 'Property',
      accessorKey: 'propertyId',
      size: 200,
      enableSorting: false,
      cell: ({ getValue }) => {
        const pid = getValue() as string | undefined;
        if (!pid) return <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>—</span>;
        const prop = propertyLookup[pid];
        return <span style={{ fontSize: '0.85rem' }}>{prop ? `${prop.propertyCode} - ${prop.propertyName}` : pid}</span>;
      },
    },
    { id: 'bedType', header: 'Bed Type', accessorKey: 'bedType', size: 120, enableSorting: true },
    { id: 'maxOccupancy', header: 'Max Occupancy', accessorKey: 'maxOccupancy', size: 130, enableSorting: true },
    { id: 'totalInventory', header: 'Total Inventory', accessorKey: 'totalInventory', size: 130, enableSorting: true },
    {
      id: 'smokingAllowed',
      header: 'Smoking',
      accessorKey: 'smokingAllowed',
      size: 100,
      cell: ({ getValue }) => {
        const allowed = getValue() as boolean;
        return (
          <span style={{ fontSize: '0.85rem', color: allowed ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            {allowed ? 'Yes' : 'No'}
          </span>
        );
      },
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    { id: 'baseOccupancy', header: 'Base Occupancy', accessorKey: 'baseOccupancy', size: 130, enableSorting: true },
    { id: 'roomView', header: 'Room View', accessorKey: 'roomView', size: 120, enableSorting: true },
    { id: 'roomSizeSqft', header: 'Room Size (sqft)', accessorKey: 'roomSizeSqft', size: 140, enableSorting: true },
  ], [propertyLookup]);

  // --- Row actions (matches standard: View, Edit, Copy, Delete) ---
  const getRowActions = useCallback((rc: RoomCategory): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rc) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rc) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rc) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(rc), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        id: 'search',
        icon: <IconSystem.search size={18} />,
        label: 'Search Room Categories',
        onClick: () => setSearchPopupOpen(true),
        tooltip: 'Search Room Categories (F2)',
        variant: 'icon',
      },
    ];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add Room Category',
        onClick: handleAddNew,
        tooltip: 'Add New Room Category',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    });
    if (allowedActions.includes('export')) {
      buttons.push({
        id: 'export',
        icon: <IconSystem.download size={18} />,
        label: 'Export',
        onClick: () => console.log('Export clicked'),
        tooltip: 'Export',
        variant: 'icon',
      });
    }
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      propertyId: filterProperty || undefined,
      bedType: filterBedType || undefined,
      roomView: filterRoomView || undefined,
      smokingAllowed: filterSmoking ? filterSmoking === 'true' : undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [filterProperty, filterBedType, filterRoomView, filterSmoking, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterProperty('');
    setFilterBedType('');
    setFilterRoomView('');
    setFilterSmoking('');
    setFilterStatus('');
    setQueryParams(prev => ({
      ...prev,
      propertyId: undefined,
      bedType: undefined,
      roomView: undefined,
      smokingAllowed: undefined,
      includeInactive: undefined,
      page: 1,
    }));
  }, []);

  // --- Async property dropdown loader for the filter panel ---
  const loadPropertyFilterOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 500,
      includeInactive: false,
    });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // --- Initial property option for modal (handles inactive property display in edit/copy) ---
  const initialPropertyOption = useMemo(() => {
    if (!selectedRecord?.propertyId) return null;
    const prop = propertyLookup[selectedRecord.propertyId];
    if (!prop) return null;
    return {
      value: prop.id,
      label: `${prop.propertyCode} - ${prop.propertyName}${prop.isActive ? '' : ' (Inactive)'}`,
    };
  }, [selectedRecord, propertyLookup]);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Room Category Master"
        description="Manage room categories and their configurations"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search room categories...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterProperty, filterBedType, filterRoomView, filterSmoking, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      {/* REQUIRED: marginTop: '6px' — the ONLY allowed inline style on .module-content */}
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No room categories found"
              frozenLeftColumns={['select', 'categoryCode']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(pagination.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  flex: 1,
                  overflow: 'auto',
                  alignContent: 'start',
                }}>
                  {data.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.tag size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No room categories found</p>
                    </div>
                  ) : (
                    data.map((rc) => (
                      <RoomCategoryCard
                        key={rc.id as string}
                        roomCategory={rc}
                        onView={handleView}
                        onEdit={handleEdit}
                        onCopy={handleCopy}
                        onDelete={handleDeleteClick}
                      />
                    ))
                  )}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={currentPageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* REQUIRED: Filter panel as SIBLING of data-grid-container */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-muted)',
                borderRadius: '0.25rem',
              }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyFilterOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterProperty}
                onChange={(val: string | string[] | null) => setFilterProperty((val as string) || '')}
                placeholder="All Properties"
                isClearable
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Bed Type"
                options={bedTypeFilterOptions}
                value={filterBedType}
                onChange={(val: string | string[] | null) => setFilterBedType((val as string) || '')}
                placeholder="Select bed type..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Room View"
                options={roomViewFilterOptions}
                value={filterRoomView}
                onChange={(val: string | string[] | null) => setFilterRoomView((val as string) || '')}
                placeholder="Select view..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Smoking Allowed"
                options={smokingFilterOptions}
                value={filterSmoking}
                onChange={(val: string | string[] | null) => setFilterSmoking((val as string) || '')}
                placeholder="Select..."
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..."
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleFilterApply}>
              Apply
            </button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <RoomCategoryViewModal
        isOpen={viewModalOpen}
        roomCategory={selectedRecord}
        onClose={() => setViewModalOpen(false)}
        onEdit={(rc) => handleEdit(rc)}
        propertyLookup={propertyLookup}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Form Modal */}
      <RoomCategoryModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
        initialPropertyOption={initialPropertyOption}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.categoryName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      {/* Room Category Search Popup (F2) */}
      <SyutSearchGrid<RoomCategory>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(rc) => handleView(rc)}
        title="Search Room Categories"
        columns={[
          { key: 'categoryCode', header: 'Code', width: '120px', sortable: true },
          { key: 'categoryName', header: 'Category Name', width: '200px', sortable: true },
          { key: 'bedType', header: 'Bed Type', width: '120px', sortable: true },
          { key: 'maxOccupancy', header: 'Max Occ.', width: '100px', sortable: true },
          {
            key: 'isActive',
            header: 'Status',
            width: '100px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Active' : 'Inactive'}
              </span>
            ),
          },
        ]}
        data={data}
        filters={[
          {
            key: 'bedType',
            label: 'Bed Type',
            type: 'select',
            options: [
              { value: 'King', label: 'King' },
              { value: 'Queen', label: 'Queen' },
              { value: 'Twin', label: 'Twin' },
              { value: 'Double', label: 'Double' },
              { value: 'Single', label: 'Single' },
            ],
          },
          {
            key: 'isActive',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ],
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="room-category-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default RoomCategoryMasterScreen;
